import React, { useState, useEffect, useMemo } from 'react';
import '../styles/SellerProfile.css';
import AddPaymentModal from '../components/modals/AddPaymentModal';
import AccountStatement from './AccountStatement';
import BalanceSheet from './BalanceSheet';
import fetchApi from '../utils/api.js';
import Swal from 'sweetalert2';
import { FaEdit, FaTrash } from 'react-icons/fa';
import SearchBar from '../components/ui/SearchBar';
import { toast } from 'react-toastify';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import AirDatePicker from '../components/ui/AirDatePicker'; // dd-mm-yyyy format

const SellerProfile = ({ seller, onBack, onPreview, onEdit, onPreviewReturn, onEditReturn, companyId }) => {
    const { hasPermission } = usePermissionContext();
    const formatDate = (dateStr) => {
        if (!dateStr) return '---';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}-${m}-${y}`;
    };
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [paymentToEdit, setPaymentToEdit] = useState(null);
    const [showStatement, setShowStatement] = useState(false);
    const [showBalanceSheet, setShowBalanceSheet] = useState(false);
    const [totals, setTotals] = useState({ totalInvoiceAmount: 0, paidAmount: 0, pendingAmount: 0 });
    const [invoices, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [challans, setChallans] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDetails, setShowDetails] = useState(false);

    // fallbacks...
    // Unified data mapping to support multiple naming conventions (backend and mocks)
    const displayData = {
        name: seller?.sellerName || seller?.seller_name || '---',
        tradeName: seller?.sellerTradeName || seller?.seller_trade_name || '---',
        email: seller?.sellerEmail || seller?.seller_email || '---',
        mobileNumber: seller?.sellerMobileNumber || seller?.seller_mobile || '---',
        gst: seller?.sellerGstNumber || seller?.seller_gst || '---',
        panNo: seller?.sellerPanCardNumber || seller?.seller_pan_no || '---',
        prefix: seller?.sellerPrefix || seller?.seller_prefix || '---',
        sellerType: seller?.sellerTypeId?.customerTypeName || seller?.seller_type || seller?.sellerType || '---',
        country: seller?.sellerCountry || seller?.seller_country || 'India',
        state: seller?.sellerState || seller?.seller_state || '---',
        city: seller?.sellerCity || seller?.seller_city || '---',
        pinCode: seller?.sellerPinCode || seller?.seller_pin_code || '---',
        stateCode: seller?.sellerStateCode || seller?.seller_state_code || '---',
        address: seller?.sellerAddress || seller?.seller_address || '---',
        bankName: seller?.sellerBankName || seller?.seller_bank_name || '---',
        accountName: seller?.sellerBankAccountName || seller?.seller_bank_account_name || seller?.seller_bank_acc_name || '---',
        accountNo: seller?.sellerAccountNo || seller?.seller_bank_account_number || seller?.seller_bank_acc_no || '---',
        ifscCode: seller?.sellerIfscCode || seller?.seller_bank_ifsc_code || seller?.seller_ifsc || '---',
        cinNumber: seller?.sellerCinNumber || seller?.seller_cin_number || '---',
        bankAddress: seller?.sellerBankAddress || seller?.seller_bank_address || '---'
    };


    // --- Invoice List State ---
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [invoicePage, setInvoicePage] = useState(1);
    const [invoiceEntries, setInvoiceEntries] = useState(10);

    // --- Payment List State ---
    const [paymentSearch, setPaymentSearch] = useState('');
    const [paymentPage, setPaymentPage] = useState(1);
    const [paymentEntries, setPaymentEntries] = useState(10);

    // --- Challan List State ---
    const [challanSearch, setChallanSearch] = useState('');
    const [challanPage, setChallanPage] = useState(1);
    const [challanEntries, setChallanEntries] = useState(10);

    // --- Challan Product List State ---
    const [productSearch, setProductSearch] = useState('');
    const [productPage, setProductPage] = useState(1);
    const [productEntries] = useState(10);

    // --- Date Filter State ---
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // --- Expense List State ---
    const [expenseSearch, setExpenseSearch] = useState('');
    const [expensePage, setExpensePage] = useState(1);
    const [expenseEntries, setExpenseEntries] = useState(10);

    // --- Kasar Payment List State ---
    const [kasarSearch, setKasarSearch] = useState('');
    const [kasarPage, setKasarPage] = useState(1);
    const [kasarEntries, setKasarEntries] = useState(10);

    // --- Purchase Return List State ---
    const [purchaseReturns, setPurchaseReturns] = useState([]);
    const [purchaseReturnSearch, setPurchaseReturnSearch] = useState('');
    const [purchaseReturnPage, setPurchaseReturnPage] = useState(1);
    const [purchaseReturnEntries, setPurchaseReturnEntries] = useState(10);

    useEffect(() => {
        if (seller) {
            fetchSellerData();
        }
    }, [seller]);

    const fetchSellerData = async () => {
        try {
            setLoading(true);
            const sId = seller.id || seller.sellerId || seller._id;

            let currentStart = startDate;
            let currentEnd = endDate;

            // Validation Logic
            if (currentStart && !currentEnd) {
                const today = new Date().toISOString().split('T')[0];
                currentEnd = today;
                setEndDate(today); // Sync UI
            } else if (!currentStart && currentEnd) {
                toast.warn('Start Date is required!');
                setLoading(false);
                return;
            }

            let queryParams = `?buyerId=${sId}`; // Backend uses buyerId for purchase payments
            if (currentStart && currentEnd) {
                queryParams += `&startDate=${currentStart}&endDate=${currentEnd}`;
            }

            // Fetch Totals
            if (hasPermission('Purchase Invoice', 'view')) {
                try {
                    const totalsData = await fetchApi(`/purchase-order-payment/totals/${sId}${currentStart && currentEnd ? `?startDate=${currentStart}&endDate=${currentEnd}` : ''}`);
                    setTotals(totalsData);
                } catch (err) { console.warn("Failed to fetch totals", err); }

                // Fetch Ledger
                try {
                    const ledger = await fetchApi(`/purchase-order-payment/ledger/${sId}${currentStart && currentEnd ? `?startDate=${currentStart}&endDate=${currentEnd}` : ''}`);

                const invList = ledger.filter(e => e.type === 'Invoice').map(e => ({
                    id: e.id,
                    seller: displayData.tradeName,
                    company: 'ASENSIA INDUSTRY LLP',
                    invoiceNo: e.refNo,
                    date: e.date,
                    amount: e.credit.toFixed(2),
                    placeOfSupply: displayData.city || '---'
                }));
                    setInvoices(invList);
                } catch (err) { console.warn("Failed to fetch ledger", err); }
            } else { setTotals({ totalInvoiceAmount: 0, paidAmount: 0, pendingAmount: 0 }); setInvoices([]); }

            // Fetch Payments separately
            if (hasPermission('Purchase Invoice', 'view')) {
                try {
                    const paymentsData = await fetchApi(`/purchase-order-payment${queryParams}`);
                    setPayments(paymentsData);
                } catch (err) { console.warn("Failed to fetch payments", err); }
            } else { setPayments([]); }

            // Fetch Challans - Vendors are purchaseCompanyId in purchase challans
            if (hasPermission('Purchase Challan', 'view')) {
                try {
                    const challansData = await fetchApi(`/purchase-challan?purchaseCompanyId=${sId}&status=Pending`);
                    const fetchedChallans = challansData.purchaseChallans || (Array.isArray(challansData) ? challansData : []);
                    setChallans(fetchedChallans);
                } catch (err) { console.warn("Failed to fetch challans", err); }
            } else { setChallans([]); }

            // Fetch Expenses
            if (hasPermission('Expense', 'view')) {
                try {
                    const expensesData = await fetchApi(`/expense?partyType=seller&partyId=${sId}&limit=1000${currentStart && currentEnd ? `&startDate=${currentStart}&endDate=${currentEnd}` : ''}`);
                    setExpenses(expensesData.expenses || []);
                } catch (err) { console.warn("Failed to fetch expenses", err); }
            } else { setExpenses([]); }

            // Fetch Purchase Returns
            if (hasPermission('PurchaseReturn', 'view')) {
                try {
                    const returnsData = await fetchApi(`/purchase-return?sellerId=${sId}&limit=1000${currentStart && currentEnd ? `&startDate=${currentStart}&endDate=${currentEnd}` : ''}`);
                    setPurchaseReturns(returnsData.purchaseReturns || (Array.isArray(returnsData) ? returnsData : []));
                } catch (err) { console.warn("Failed to fetch returns", err); }
            } else { setPurchaseReturns([]); }

        } finally {
            setLoading(false);
        }
    };

    // --- Helper Functions ---
    const filterData = (list, search, keys) => {
        if (!search) return list;
        return list.filter(item =>
            keys.some(key => item[key] && item[key].toString().toLowerCase().includes(search.toLowerCase()))
        );
    };

    // Filter Invoices
    const filteredInvoices = filterData(invoices, invoiceSearch, ['seller', 'company', 'invoiceNo', 'date']);
    const invoiceLastIdx = invoicePage * invoiceEntries;
    const invoiceFirstIdx = invoiceLastIdx - invoiceEntries;
    const currentInvoices = filteredInvoices.slice(invoiceFirstIdx, invoiceLastIdx);

    // Filter Payments
    const filteredPayments = filterData(payments, paymentSearch, ['paymentAmount', 'paymentModeName', 'paymentCollectorName']);
    const paymentLastIdx = paymentPage * paymentEntries;
    const paymentFirstIdx = paymentLastIdx - paymentEntries;
    const currentPayments = filteredPayments.slice(paymentFirstIdx, paymentLastIdx);

    // Filter Expenses
    const filteredExpenses = filterData(expenses, expenseSearch, ['expenseName', 'invoiceNo', 'expensePurpose', 'date', 'expenseTypeName']);
    const expenseLastIdx = expensePage * expenseEntries;
    const expenseFirstIdx = expenseLastIdx - expenseEntries;
    const currentExpenses = filteredExpenses.slice(expenseFirstIdx, expenseLastIdx);

    // Filter Kasar Payments (where kasarAmount > 0)
    const kasarPayments = useMemo(() => {
        return payments.filter(pay => Number(pay.kasarAmount || pay.kasar_amount || 0) > 0);
    }, [payments]);

    const filteredKasarPayments = filterData(kasarPayments, kasarSearch, ['kasarAmount', 'paymentAmount', 'paymentModeName', 'remark']);
    const kasarLastIdx = kasarPage * kasarEntries;
    const kasarFirstIdx = kasarLastIdx - kasarEntries;
    const currentKasarPayments = filteredKasarPayments.slice(kasarFirstIdx, kasarLastIdx);

    // Filter Purchase Returns
    const filteredPurchaseReturns = useMemo(() => {
        return purchaseReturns.filter(ret => {
            const searchLower = purchaseReturnSearch.toLowerCase();
            return (
                (ret.return_no && ret.return_no.toLowerCase().includes(searchLower)) ||
                (ret.original_invoice_no && ret.original_invoice_no.toLowerCase().includes(searchLower)) ||
                (ret.note && ret.note.toLowerCase().includes(searchLower)) ||
                (ret.total_amount && String(ret.total_amount).includes(searchLower))
            );
        });
    }, [purchaseReturns, purchaseReturnSearch]);

    const purchaseReturnLastIdx = purchaseReturnPage * purchaseReturnEntries;
    const purchaseReturnFirstIdx = purchaseReturnLastIdx - purchaseReturnEntries;
    const currentPurchaseReturns = filteredPurchaseReturns.slice(purchaseReturnFirstIdx, purchaseReturnLastIdx);

    // Filter & Format Challans (One row per Challan)
    const challanList = useMemo(() => {
        return challans.map(ch => {
            let chItems = [];
            try {
                chItems = typeof ch.items === 'string' ? JSON.parse(ch.items) : (ch.items || []);
            } catch (e) { console.error(e); }

            const totalQty = chItems.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);

            return {
                id: ch.id || ch._id,
                challanNo: ch.invoiceNo || ch.challan_no,
                date: ch.invoiceDate || ch.challan_date,
                totalItems: chItems.length,
                totalQty: totalQty,
                fullChallan: ch,
                items: chItems
            };
        });
    }, [challans]);

    const filteredChallans = filterData(challanList, challanSearch, ['challanNo', 'date']);
    const challanLastIdx = challanPage * challanEntries;
    const challanFirstIdx = challanLastIdx - challanEntries;
    const currentChallans = filteredChallans.slice(challanFirstIdx, challanLastIdx);

    // Flatten all pending challan items into individual product rows
    const flattenedChallanProducts = useMemo(() => {
        const products = [];
        challans.forEach(ch => {
            let chItems = [];
            try {
                chItems = typeof ch.items === 'string' ? JSON.parse(ch.items) : (ch.items || []);
            } catch (e) { console.error(e); }

            if (Array.isArray(chItems)) {
                chItems.forEach((it, idx) => {
                    if (!it) return;
                    products.push({
                        uniqueId: `${ch.id || ch._id}-${idx}`,
                        challanId: ch.id || ch._id,
                        challanNo: ch.challan_no || ch.invoiceNo,
                        date: ch.challan_date || ch.invoiceDate,
                        productName: it.product || it.productName || '',
                        category: it.category || '',
                        hsnCode: it.hsnCode || it.productHsnCode || '',
                        grade: it.grade || '',
                        color: it.color || '',
                        unit: it.unit || '',
                        quantity: it.quantity || 0,
                        rate: it.rate || 0,
                        total: it.total || 0,
                        fullChallan: ch,
                        rawItem: it
                    });
                });
            }
        });
        return products;
    }, [challans]);

    const filteredProducts = filterData(flattenedChallanProducts, productSearch, ['productName', 'challanNo', 'category', 'grade']);
    const productLastIdx = productPage * productEntries;
    const productFirstIdx = productLastIdx - productEntries;
    const currentProducts = filteredProducts.slice(productFirstIdx, productLastIdx);

    const handleDeletePayment = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await fetchApi(`/purchase-order-payment/${id}`, { method: 'DELETE' });
                Swal.fire('Deleted!', 'Payment has been deleted.', 'success');
                fetchSellerData();
            } catch (error) {
                console.error("Delete Error:", error);
                Swal.fire('Error!', 'Failed to delete payment.', 'error');
            }
        }
    };

    const handleDeletePurchaseReturn = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Delete this purchase return? This will also revert stock adjustments.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await fetchApi(`/purchase-return/${id}`, { method: 'DELETE' });
                Swal.fire('Deleted!', 'Purchase return has been deleted.', 'success');
                fetchSellerData();
            } catch (error) {
                console.error("Delete Error:", error);
                Swal.fire('Error!', 'Failed to delete purchase return.', 'error');
            }
        }
    };

    const handleEditPayment = (payment) => {
        setPaymentToEdit(payment);
        setIsPaymentModalOpen(true);
    };

    const handleConvertAllChallans = () => {
        if (filteredChallans.length === 0) {
            toast.info("No challans available to convert");
            return;
        }

        // Get all unique challan IDs from the filtered list
        const sourceChallanIds = filteredChallans.map(ch => ch.id);

        // Merge ALL items from ALL filtered challans
        const allItems = [];
        filteredChallans.forEach(ch => {
            if (Array.isArray(ch.items)) {
                ch.items.forEach(it => {
                    allItems.push({
                        ...it,
                        product: it.product || it.productName,
                        sourceChallanId: ch.id
                    });
                });
            }
        });

        if (allItems.length === 0) return;

        const base = filteredChallans[0].fullChallan;
        const mergedData = {
            ...base,
            items: allItems,
            isBatchConversion: true,
            isFromChallan: true,
            challanId: base.id || base._id,
            sourceChallanIds: sourceChallanIds
        };

        const event = new CustomEvent('convertChallansToInvoice', { detail: mergedData });
        window.dispatchEvent(event);
    };

    // Convert ALL flattened products from the product list
    const handleConvertAllProducts = () => {
        if (filteredProducts.length === 0) {
            toast.info("No products available to convert");
            return;
        }

        const sourceChallanIds = [...new Set(filteredProducts.map(p => p.challanId))];
        const allItems = filteredProducts.map(p => ({
            ...p.rawItem,
            product: p.productName,
            category: p.category,
            hsnCode: p.hsnCode,
            grade: p.grade,
            color: p.color,
            unit: p.unit,
            quantity: p.quantity,
            rate: p.rate,
            total: p.total,
            sourceChallanId: p.challanId
        }));

        const base = filteredProducts[0].fullChallan;
        const mergedData = {
            ...base,
            items: allItems,
            isBatchConversion: true,
            isFromChallan: true,
            challanId: base.id || base._id,
            sourceChallanIds: sourceChallanIds
        };

        const event = new CustomEvent('convertChallansToInvoice', { detail: mergedData });
        window.dispatchEvent(event);
    };

    const handleConvertSingleRow = (row) => {
        const detail = {
            ...row.fullChallan,
            isFromChallan: true,
            challanId: row.fullChallan.id || row.fullChallan._id
        };
        const event = new CustomEvent('convertChallansToInvoice', { detail });
        window.dispatchEvent(event);
    };

    const handlePreviewClick = async (inv) => {
        try {
            // inv.id is the database _id from ledger
            const fullInvoice = await fetchApi(`/purchase-order/${inv.id}`);
            if (fullInvoice && onPreview) {
                onPreview(fullInvoice);
            }
        } catch (error) {
            console.error("Error fetching full purchase invoice for preview:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch purchase invoice details for preview.'
            });
        }
    };

    if (showStatement) {
        return (
            <AccountStatement
                customer={displayData}
                customerId={seller.id || seller.sellerId || seller._id}
                isPurchase={true}
                startDate={startDate}
                endDate={endDate}
                onBack={() => setShowStatement(false)}
            />
        );
    }

    if (showBalanceSheet) {
        return (
            <BalanceSheet
                customer={displayData}
                customerId={seller.id || seller.sellerId || seller._id}
                isPurchase={true}
                startDate={startDate}
                endDate={endDate}
                onBack={() => setShowBalanceSheet(false)}
                companyId={companyId}
            />
        );
    }





    return (
        <div className="seller-profile-container">
            <div className="seller-profile-header">
                <h2 className="seller-profile-title">
                    {(displayData.tradeName && displayData.tradeName !== '---') ? displayData.tradeName : (displayData.name || 'Seller Profile')}
                </h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {hasPermission('Seller', 'update') && onEdit && (
                        <button className="seller-profile-back-btn" onClick={onEdit} style={{ backgroundColor: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <FaEdit /> Edit
                        </button>
                    )}
                    <button
                        className="seller-profile-back-btn"
                        onClick={() => setShowDetails(!showDetails)}
                        style={{ backgroundColor: showDetails ? '#ef4444' : '#10b981', color: 'white' }}
                    >
                        {showDetails ? 'Closed Details' : 'Open Details'}
                    </button>
                    {onBack && (
                        <button className="seller-profile-back-btn" onClick={onBack}>
                            ← Back
                        </button>
                    )}
                </div>
            </div>

            {/* Date Filter Section (Red box from user image) */}
            <div className="profile-date-filter-row">
                <div className="date-input-group">
                    <label>From Date:</label>
                    <AirDatePicker
                        value={startDate}
                        onChange={(dateStr) => setStartDate(dateStr)}
                    />
                </div>
                <div className="date-input-group">
                    <label>To Date:</label>
                    <AirDatePicker
                        value={endDate}
                        onChange={(dateStr) => setEndDate(dateStr)}
                    />
                </div>
                <div className="filter-actions-group">
                    <button className="btn-filter-apply" onClick={fetchSellerData}>Apply Filter</button>
                    <button className="btn-filter-clear" onClick={() => { setStartDate(''); setEndDate(''); fetchSellerData(); }}>Clear</button>
                </div>
            </div>

            {/* Top Details Section - 3 Columns */}
            {showDetails && (
                <div className="seller-profile-details-grid">
                    {/* Personal & Business Details */}
                    <div className="seller-detail-card">
                        <div className="seller-detail-card-title">Personal & Business Details</div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Trade Name:</span>
                            <span className="seller-detail-value">{displayData.tradeName}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Name:</span>
                            <span className="seller-detail-value">{displayData.name}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Prefix:</span>
                            <span className="seller-detail-value">{displayData.prefix}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Email:</span>
                            <span className="seller-detail-value">{displayData.email}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Mobile:</span>
                            <span className="seller-detail-value">{displayData.mobileNumber}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">GST:</span>
                            <span className="seller-detail-value">{displayData.gst}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">PAN No:</span>
                            <span className="seller-detail-value">{displayData.panNo}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Seller Type:</span>
                            <span className="seller-detail-value">{displayData.sellerType}</span>
                        </div>
                    </div>

                    {/* Address Details */}
                    <div className="seller-detail-card">
                        <div className="seller-detail-card-title">Address Details</div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Country:</span>
                            <span className="seller-detail-value">{displayData.country}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">State:</span>
                            <span className="seller-detail-value">{displayData.state}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">City:</span>
                            <span className="seller-detail-value">{displayData.city}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Pin Code:</span>
                            <span className="seller-detail-value">{displayData.pinCode}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">State Code:</span>
                            <span className="seller-detail-value">{displayData.stateCode}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Address:</span>
                            <span className="seller-detail-value" style={{ maxWidth: '60%' }}>{displayData.address}</span>
                        </div>
                    </div>

                    {/* Bank Details */}
                    <div className="seller-detail-card">
                        <div className="seller-detail-card-title">Bank Details</div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Bank Name:</span>
                            <span className="seller-detail-value">{displayData.bankName}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Account Name:</span>
                            <span className="seller-detail-value">{displayData.accountName}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Account No:</span>
                            <span className="seller-detail-value">{displayData.accountNo}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">IFSC Code:</span>
                            <span className="seller-detail-value">{displayData.ifscCode}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">CIN Number:</span>
                            <span className="seller-detail-value">{displayData.cinNumber}</span>
                        </div>
                        <div className="seller-detail-row">
                            <span className="seller-detail-label">Bank Address:</span>
                            <span className="seller-detail-value">{displayData.bankAddress}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Statistics Row */}
            <div className="seller-stats-row">
                <div className="seller-stat-card">
                    <div className="seller-stat-icon-wrapper stat-blue">
                        💵
                    </div>
                    <div className="seller-stat-info">
                        <h3>{(totals.totalInvoiceAmount || 0).toFixed(2)}/-</h3>
                        <p className="seller-stat-label">Credit</p>
                    </div>
                </div>
                <div className="seller-stat-card">
                    <div className="seller-stat-icon-wrapper stat-green">
                        📄
                    </div>
                    <div className="seller-stat-info">
                        <h3>{(totals.paidAmount || 0).toFixed(2)}/-</h3>
                        <p className="seller-stat-label">Debit</p>
                    </div>
                </div>
                <div className="seller-stat-card">
                    <div className="seller-stat-icon-wrapper stat-red">
                        📱
                    </div>
                    <div className="seller-stat-info">
                        <h3>{(totals.pendingAmount || 0).toFixed(2)}/-</h3>
                        <p className="seller-stat-label">
                            {totals.pendingAmount > 0 ? 'Pending' : totals.pendingAmount < 0 ? 'Advance' : 'Balance'}
                        </p>
                    </div>
                </div>

                <div className="seller-profile-actions">
                    <button className="seller-action-btn" onClick={() => setIsPaymentModalOpen(true)}>Add Payment</button>
                    <button className="seller-action-btn" onClick={() => setShowBalanceSheet(true)}>Balance Sheet</button>
                    <button className="seller-action-btn" onClick={() => setShowStatement(true)}>Account Statement</button>
                </div>
            </div>

            {/* Tables Area */}
            <div className="seller-profile-tables-wrapper">

                {/* Purchase Challan Invoice List (New Section) */}
                {hasPermission('Purchase Challan', 'view') && (
                <div className="seller-list-section full-width">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="seller-list-header" style={{ margin: 0 }}>Purchase Challan Invoice List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={challanSearch}
                                onChange={(e) => {
                                    setChallanSearch(e.target.value);
                                    setChallanPage(1);
                                }}
                                placeholder="Search challans..."
                            />
                            <button className="btn-batch-convert" style={{ position: 'static' }} onClick={handleConvertAllChallans}>
                                Purchase Challan To Purchase Invoice
                            </button>
                        </div>
                    </div>

                    <table className="seller-profile-table">
                        <thead>
                            <tr>
                                <th>CHALLAN NO</th>
                                <th>DATE</th>
                                <th>TOTAL ITEMS</th>
                                <th>TOTAL QTY</th>
                                <th style={{ width: '100px' }}>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentChallans.length > 0 ? currentChallans.map((ch, idx) => (
                                <tr key={ch.id || idx}>
                                    <td style={{ fontWeight: '500', color: '#1e3a8a' }}>{ch.challanNo}</td>
                                    <td>{formatDate(ch.date)}</td>
                                    <td>{ch.totalItems}</td>
                                    <td>{parseFloat(ch.totalQty || 0).toFixed(2)}/-</td>
                                    <td>
                                        <button
                                            className="btn-row-convert"
                                            title="Convert this challan"
                                            onClick={() => handleConvertSingleRow(ch)}
                                        >
                                            Convert
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={5} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-info">
                            Showing {filteredChallans.length === 0 ? 0 : challanFirstIdx + 1} to {Math.min(challanLastIdx, filteredChallans.length)} of {filteredChallans.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={challanPage <= 1}
                                onClick={() => setChallanPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredChallans.length / challanEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === challanPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setChallanPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={challanPage >= Math.ceil(filteredChallans.length / challanEntries)}
                                onClick={() => setChallanPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Purchase Challan Product List */}
                {hasPermission('Purchase Challan', 'view') && (
                <div className="seller-list-section full-width">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="seller-list-header" style={{ margin: 0 }}>Purchase Challan Product List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={productSearch}
                                onChange={(e) => {
                                    setProductSearch(e.target.value);
                                    setProductPage(1);
                                }}
                                placeholder="Search products..."
                            />
                            <button className="btn-batch-convert" style={{ position: 'static' }} onClick={handleConvertAllProducts}>
                                Convert All Products To Invoice
                            </button>
                        </div>
                    </div>

                    <table className="seller-profile-table">
                        <thead>
                            <tr>
                                <th>CHALLAN NO</th>
                                <th>PRODUCT NAME</th>
                                <th>CATEGORY</th>
                                <th>GRADE</th>
                                <th>COLOR</th>
                                <th>QTY</th>
                                <th>RATE</th>
                                <th>TOTAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentProducts.length > 0 ? currentProducts.map((p, idx) => (
                                <tr key={p.uniqueId || idx}>
                                    <td style={{ fontWeight: '500', color: '#1e3a8a' }}>{p.challanNo}</td>
                                    <td style={{ fontWeight: '500' }}>{p.productName}</td>
                                    <td>{p.category || '---'}</td>
                                    <td>{p.grade || '---'}</td>
                                    <td>{p.color || '---'}</td>
                                    <td>{parseFloat(p.quantity || 0).toFixed(2)}</td>
                                    <td>₹{parseFloat(p.rate || 0).toFixed(2)}</td>
                                    <td>₹{parseFloat(p.total || 0).toFixed(2)}</td>
                                </tr>
                            )) : (
                                <tr><td colSpan={8} className="no-data">No pending products available</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-info">
                            Showing {filteredProducts.length === 0 ? 0 : productFirstIdx + 1} to {Math.min(productLastIdx, filteredProducts.length)} of {filteredProducts.length} entries
                        </div>
                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={productPage <= 1}
                                onClick={() => setProductPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredProducts.length / productEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === productPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setProductPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={productPage >= Math.ceil(filteredProducts.length / productEntries)}
                                onClick={() => setProductPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Second Row of Tables: Invoices and Payments side-by-side */}
                <div className="seller-profile-tables-grid">
                    {/* Purchase Invoice List */}
                    {hasPermission('Purchase Invoice', 'view') && (
                    <div className="seller-list-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                            <div className="seller-list-header" style={{ margin: 0 }}>Purchase Invoice List</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <SearchBar
                                    value={invoiceSearch}
                                    onChange={(e) => {
                                        setInvoiceSearch(e.target.value);
                                        setInvoicePage(1);
                                    }}
                                    placeholder="Search invoices..."
                                />
                            </div>
                        </div>

                        <table className="seller-profile-table">
                            <thead>
                                <tr>
                                    <th>INVOICE NO</th>
                                    <th>INVOICE DATE</th>
                                    <th>PLACE OF SUPPLY</th>
                                    <th>TOTAL AMOUNT</th>
                                    <th>PREVIEW</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentInvoices.length > 0 ? currentInvoices.map((inv, idx) => (
                                    <tr key={idx}>
                                        <td>{inv.invoiceNo}</td>
                                        <td>{formatDate(inv.date)}</td>
                                        <td>{inv.placeOfSupply}</td>
                                        <td>₹{inv.amount}</td>
                                        <td>
                                            <button
                                                className="list-page-icon-btn"
                                                title="Preview"
                                                onClick={() => handlePreviewClick(inv)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                    <circle cx="12" cy="12" r="3"></circle>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={5} className="no-data">No data available in table</td></tr>
                                )}
                            </tbody>
                        </table>

                        <div className="list-page-footer">
                            <div className="list-page-entries">
                                <span>Show</span>
                                <select
                                    className="list-page-entries-select"
                                    value={invoiceEntries}
                                    onChange={(e) => {
                                        setInvoiceEntries(Number(e.target.value));
                                        setInvoicePage(1);
                                    }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                                <span>entries</span>
                            </div>

                            <div className="list-page-info">
                                Showing {filteredInvoices.length === 0 ? 0 : invoiceFirstIdx + 1} to {Math.min(invoiceLastIdx, filteredInvoices.length)} of {filteredInvoices.length} entries
                            </div>

                            <div className="list-page-pagination">
                                <button
                                    type="button"
                                    className="list-page-page-btn"
                                    disabled={invoicePage <= 1}
                                    onClick={() => setInvoicePage(p => Math.max(1, p - 1))}
                                >
                                    Previous
                                </button>
                                {Array.from({ length: Math.ceil(filteredInvoices.length / invoiceEntries) }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        type="button"
                                        className={`list-page-page-num ${p === invoicePage ? 'list-page-page-num-active' : ''}`}
                                        onClick={() => setInvoicePage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className="list-page-page-btn"
                                    disabled={invoicePage >= Math.ceil(filteredInvoices.length / invoiceEntries)}
                                    onClick={() => setInvoicePage(p => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Payment List */}
                    {hasPermission('Purchase Invoice', 'view') && (
                    <div className="seller-list-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                            <div className="seller-list-header" style={{ margin: 0 }}>Payment List</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <SearchBar
                                    value={paymentSearch}
                                    onChange={(e) => {
                                        setPaymentSearch(e.target.value);
                                        setPaymentPage(1);
                                    }}
                                    placeholder="Search payments..."
                                />
                            </div>
                        </div>

                        <table className="seller-profile-table">
                            <thead>
                                <tr>
                                    <th>AMOUNT</th>
                                    <th>DATE TIME</th>
                                    <th>MODE</th>
                                    <th>COLLECTOR</th>
                                    <th>ACTION</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentPayments.length > 0 ? currentPayments.map((pay, idx) => (
                                    <tr key={idx}>
                                        <td>{Number(pay.paymentAmount).toFixed(2)}</td>
                                        <td>{pay.paymentDate} {pay.paymentTime}</td>
                                        <td>{pay.paymentModeName}</td>
                                        <td>{pay.paymentCollectorName || 'Authorized'}</td>
                                        <td>
                                            <div className="list-page-action-icons">
                                                {hasPermission('Purchase Invoice', 'update') && (
                                                    <button className="list-page-icon-btn" title="Edit" onClick={() => handleEditPayment(pay)}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                        </svg>
                                                    </button>
                                                )}
                                                {hasPermission('Purchase Invoice', 'delete') && (
                                                    <button className="list-page-icon-btn" title="Delete" onClick={() => handleDeletePayment(pay._id)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={5} className="no-data">No data available in table</td></tr>
                                )}
                            </tbody>
                        </table>

                        <div className="list-page-footer">
                            <div className="list-page-entries">
                                <span>Show</span>
                                <select
                                    className="list-page-entries-select"
                                    value={paymentEntries}
                                    onChange={(e) => {
                                        setPaymentEntries(Number(e.target.value));
                                        setPaymentPage(1);
                                    }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                                <span>entries</span>
                            </div>

                            <div className="list-page-info">
                                Showing {filteredPayments.length === 0 ? 0 : paymentFirstIdx + 1} to {Math.min(paymentLastIdx, filteredPayments.length)} of {filteredPayments.length} entries
                            </div>

                            <div className="list-page-pagination">
                                <button
                                    type="button"
                                    className="list-page-page-btn"
                                    disabled={paymentPage <= 1}
                                    onClick={() => setPaymentPage(p => Math.max(1, p - 1))}
                                >
                                    Previous
                                </button>
                                {Array.from({ length: Math.ceil(filteredPayments.length / paymentEntries) }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        type="button"
                                        className={`list-page-page-num ${p === paymentPage ? 'list-page-page-num-active' : ''}`}
                                        onClick={() => setPaymentPage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className="list-page-page-btn"
                                    disabled={paymentPage >= Math.ceil(filteredPayments.length / paymentEntries)}
                                    onClick={() => setPaymentPage(p => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                    )}
                </div>

                {/* Kasar Payment List */}
                {hasPermission('Purchase Invoice', 'view') && (
                <div className="seller-list-section full-width">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="seller-list-header" style={{ margin: 0, fontWeight: '600', fontSize: '18px' }}>Kasar Payment List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={kasarSearch}
                                onChange={(e) => {
                                    setKasarSearch(e.target.value);
                                    setKasarPage(1);
                                }}
                                placeholder="Search kasar payments..."
                            />
                        </div>
                    </div>

                    <table className="seller-profile-table">
                        <thead>
                            <tr>
                                <th>KASAR AMOUNT</th>
                                <th>PAYMENT AMOUNT</th>
                                <th>DATE TIME</th>
                                <th>MODE</th>
                                <th>REMARK</th>
                                <th>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentKasarPayments.length > 0 ? currentKasarPayments.map((pay, idx) => (
                                <tr key={idx}>
                                    <td style={{ fontWeight: '600', color: '#dc2626' }}>₹{Number(pay.kasarAmount || pay.kasar_amount || 0).toFixed(2)}</td>
                                    <td>₹{Number(pay.paymentAmount).toFixed(2)}</td>
                                    <td>{pay.paymentDate} {pay.paymentTime}</td>
                                    <td>{pay.paymentModeName}</td>
                                    <td>{pay.remark || '---'}</td>
                                    <td>
                                        <div className="list-page-action-icons">
                                            {hasPermission('Purchase Invoice', 'update') && (
                                                <button className="list-page-icon-btn" title="Edit" onClick={() => handleEditPayment(pay)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>
                                            )}
                                            {hasPermission('Purchase Invoice', 'delete') && (
                                                <button className="list-page-icon-btn" title="Delete" onClick={() => handleDeletePayment(pay._id)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={6} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={kasarEntries}
                                onChange={(e) => {
                                    setKasarEntries(Number(e.target.value));
                                    setKasarPage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredKasarPayments.length === 0 ? 0 : kasarFirstIdx + 1} to {Math.min(kasarLastIdx, filteredKasarPayments.length)} of {filteredKasarPayments.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={kasarPage <= 1}
                                onClick={() => setKasarPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredKasarPayments.length / kasarEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === kasarPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setKasarPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={kasarPage >= Math.ceil(filteredKasarPayments.length / kasarEntries)}
                                onClick={() => setKasarPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Purchase Return List */}
                {hasPermission('PurchaseReturn', 'view') && (
                <div className="seller-list-section full-width">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="seller-list-header" style={{ margin: 0 }}>Purchase Return List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={purchaseReturnSearch}
                                onChange={(e) => {
                                    setPurchaseReturnSearch(e.target.value);
                                    setPurchaseReturnPage(1);
                                }}
                                placeholder="Search purchase returns..."
                            />
                        </div>
                    </div>

                    <table className="seller-profile-table">
                        <thead>
                            <tr>
                                <th>RETURN NO</th>
                                <th>RETURN DATE</th>
                                <th>PURCHASE NO</th>
                                <th>TAXABLE AMOUNT</th>
                                <th>TOTAL AMOUNT</th>
                                <th>NOTE</th>
                                <th>ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentPurchaseReturns.length > 0 ? currentPurchaseReturns.map((ret, idx) => (
                                <tr key={ret._id || ret.id || idx}>
                                    <td style={{ fontWeight: '600', color: '#0c3447' }}>{ret.return_no}</td>
                                    <td>{formatDate(ret.return_date)}</td>
                                    <td>{ret.original_invoice_no || '---'}</td>
                                    <td>₹{parseFloat(ret.taxable_amount || 0).toFixed(2)}</td>
                                    <td style={{ fontWeight: '600' }}>₹{parseFloat(ret.total_amount || 0).toFixed(2)}</td>
                                    <td>{ret.note || '---'}</td>
                                    <td>
                                        <div className="list-page-action-icons">
                                            {hasPermission('PurchaseReturn', 'view') && (
                                                <button
                                                    className="list-page-icon-btn"
                                                    title="Preview"
                                                    onClick={() => onPreviewReturn && onPreviewReturn(ret)}
                                                    style={{ marginRight: '8px' }}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </svg>
                                                </button>
                                            )}
                                            {hasPermission('PurchaseReturn', 'update') && (
                                                <button
                                                    className="list-page-icon-btn"
                                                    title="Edit"
                                                    onClick={() => onEditReturn && onEditReturn(ret)}
                                                    style={{ marginRight: '8px' }}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>
                                            )}
                                            {hasPermission('PurchaseReturn', 'delete') && (
                                                <button
                                                    className="list-page-icon-btn"
                                                    title="Delete"
                                                    onClick={() => handleDeletePurchaseReturn(ret._id || ret.id)}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={7} className="no-data">No purchase returns available</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={purchaseReturnEntries}
                                onChange={(e) => {
                                    setPurchaseReturnEntries(Number(e.target.value));
                                    setPurchaseReturnPage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredPurchaseReturns.length === 0 ? 0 : purchaseReturnFirstIdx + 1} to {Math.min(purchaseReturnLastIdx, filteredPurchaseReturns.length)} of {filteredPurchaseReturns.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={purchaseReturnPage <= 1}
                                onClick={() => setPurchaseReturnPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredPurchaseReturns.length / purchaseReturnEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === purchaseReturnPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setPurchaseReturnPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={purchaseReturnPage >= Math.ceil(filteredPurchaseReturns.length / purchaseReturnEntries)}
                                onClick={() => setPurchaseReturnPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Expense List */}
                {hasPermission('Expense', 'view') && (
                <div className="seller-list-section full-width">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="seller-list-header" style={{ margin: 0 }}>Expense List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={expenseSearch}
                                onChange={(e) => {
                                    setExpenseSearch(e.target.value);
                                    setExpensePage(1);
                                }}
                                placeholder="Search expenses..."
                            />
                        </div>
                    </div>

                    <table className="seller-profile-table">
                        <thead>
                            <tr>
                                <th>EXPENSE TYPE</th>
                                <th>DATE</th>
                                <th>EXPENSE NAME</th>
                                <th>INVOICE NO</th>
                                <th>NET AMOUNT</th>
                                <th>GST AMOUNT</th>
                                <th>TOTAL AMOUNT</th>
                                <th>PURPOSE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentExpenses.length > 0 ? currentExpenses.map((exp, idx) => (
                                <tr key={exp.id || idx}>
                                    <td style={{ fontWeight: '500', color: '#1e3a8a' }}>{exp.expenseTypeName || '---'}</td>
                                    <td>{formatDate(exp.date)}</td>
                                    <td style={{ fontWeight: '500' }}>{exp.expenseName || '---'}</td>
                                    <td>{exp.invoiceNo || '---'}</td>
                                    <td>₹{parseFloat(exp.netAmount || 0).toFixed(2)}</td>
                                    <td>₹{parseFloat(exp.gstAmount || 0).toFixed(2)}</td>
                                    <td style={{ fontWeight: '600' }}>₹{parseFloat(exp.rowTotal || 0).toFixed(2)}</td>
                                    <td>{exp.expensePurpose || '---'}</td>
                                </tr>
                            )) : (
                                <tr><td colSpan={8} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={expenseEntries}
                                onChange={(e) => {
                                    setExpenseEntries(Number(e.target.value));
                                    setExpensePage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredExpenses.length === 0 ? 0 : expenseFirstIdx + 1} to {Math.min(expenseLastIdx, filteredExpenses.length)} of {filteredExpenses.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={expensePage <= 1}
                                onClick={() => setExpensePage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredExpenses.length / expenseEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === expensePage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setExpensePage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={expensePage >= Math.ceil(filteredExpenses.length / expenseEntries)}
                                onClick={() => setExpensePage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

            </div>

            {/* Add Payment Modal */}
            <AddPaymentModal
                isOpen={isPaymentModalOpen}
                onClose={() => {
                    setIsPaymentModalOpen(false);
                    setPaymentToEdit(null);
                    fetchSellerData();
                }}
                entityName={displayData.tradeName}
                entityType="Seller"
                entityId={seller?.id || seller?.sellerId || seller?._id}
                paymentToEdit={paymentToEdit}
            />
        </div>
    );
};

export default SellerProfile;
